/**
 * ═══════════════════════════════════════════════════════════════════════
 * 🔒🔒🔒  ARCHIVO BLOQUEADO POR ORDEN DEL CREADOR  🔒🔒🔒
 * ═══════════════════════════════════════════════════════════════════════
 * NO MODIFICAR sin autorización explícita del usuario.
 * Para desbloquear, el usuario debe identificarse como creador
 * (ej. "yo soy el creador"). Ver: /app/memory/LOCKED_FILES.md
 * ═══════════════════════════════════════════════════════════════════════
 *
 * 🟦 V11.5 — Marketplace Panel (modal).
 *
 * Muestra recetas públicas curadas. Importar copia al store local
 * (recipesStore) con el appId actual. Like es best-effort vs backend.
 *
 * 🟥 Fix UX 2026-02-19 — Cuando el usuario está fuera del Builder
 * (no hay appId), en vez de bloquear la importación abrimos un
 * selector con sus apps para elegir destino y navegar al Builder.
 */
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Download, Heart, Loader2, Sparkles, Tag, Flame, TrendingUp, AlertTriangle, FolderOpen, Plus, ChevronRight } from 'lucide-react';
import { fetchMarketplaceRecipes, likeMarketplaceRecipe, importMarketplaceRecipe } from './marketplaceBridge';
import { getToken } from '../../../utils/authToken';
import { isUserPremium, showPremiumGateMessage } from '../../../genio/premiumGate';
// 🟦 CUADRO NEGRO #1 FASE 3 sec 3.2 — Modal universal compartido.
import ExitConfirmModal from '../../../components/system/ExitConfirmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 — Helper que ESPERA explícitamente a
 * que el flag global de sesión (`window.__damyxia_session_ready`) esté en
 * `true` antes de resolver. Si ya está listo, resuelve sincrónicamente.
 * Si no (independientemente de si el flag es `false` o `undefined`),
 * escucha el evento `damyxia:session-ready` con timeout máximo de `maxMs`.
 *
 * FIX iter 310: ANTES, si el flag era `false` (porque el /auth/me pre-login
 * falló) hacíamos `Promise.resolve(false)` inmediatamente y el race
 * condition se reproducía justo tras el login. Ahora `false` y `undefined`
 * se tratan igual: esperar el evento. Solo `true` resuelve sincrónicamente.
 */
function waitForSessionReady(maxMs = 2000) {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.__damyxia_session_ready === true) return Promise.resolve(true);
  return new Promise((resolve) => {
    let done = false;
    const finish = (val) => {
      if (done) return;
      done = true;
      window.removeEventListener('damyxia:session-ready', onReady);
      resolve(val);
    };
    const onReady = () => finish(true);
    window.addEventListener('damyxia:session-ready', onReady, { once: true });
    setTimeout(() => {
      // Timeout → seguir adelante con el valor actual del flag.
      // El retry-on-401 + preflightAuth actúan como red de seguridad.
      finish(Boolean(window.__damyxia_session_ready));
    }, maxMs);
  });
}

/**
 * 🟥 CUADRO NEGRO FIX FINAL 2026-02-26 — `preflightAuth` ELIMINADO.
 * Razón: añadía un punto de fallo extra. Si /api/auth/me fallaba
 * transitoriamente (cold start, blip, timeout), mostrábamos "Tu sesión
 * expiró" SIN siquiera intentar la operación real. Ahora cada call site
 * va directo al endpoint que necesita, con retry-on-401 + backoff
 * exponencial como red de seguridad.
 */

const MarketplacePanel = ({ open, onClose, appId, es = true }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [mostLoved, setMostLoved] = useState([]);
  const [error, setError] = useState(null);
  const [importState, setImportState] = useState({}); // {id: 'ok'|'err'|'loading'|'no-app'}
  const [likeState, setLikeState] = useState({});     // {id: count}
  const [pendingRecipe, setPendingRecipe] = useState(null); // receta esperando elección de app
  const [userApps, setUserApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  // 🟥 Fix Bug Marketplace 2026-02-20 — Crear app + importar receta sin pasar por /my-apps.
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const [createError, setCreateError] = useState(null);
  // 🟦 CUADRO NEGRO #1 FASE 3 sec 3.2 — Modal X universal montado en Marketplace.
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const hasAppContext = Boolean(appId);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 — Esperar a que la sesión esté
    // lista ANTES de fetch a marketplace. El endpoint /recipes/marketplace
    // requiere auth (credentials: include) y en Prod fallaba con 401 si el
    // panel se abría antes de que /auth/me terminara de hidratarse.
    waitForSessionReady(2000).then(() => {
      if (cancelled) return;
      fetchMarketplaceRecipes().then((r) => {
        if (cancelled) return;
        if (!r.ok) {
          setError(r.error || 'fetch_failed');
          setRecipes([]);
          setMostLoved([]);
        } else {
          setRecipes(r.recipes);
          setMostLoved(r.mostLoved || []);
          const initLikes = {};
          for (const x of r.recipes) initLikes[x.id] = Number(x.likes || 0);
          setLikeState(initLikes);
        }
        setLoading(false);
      });
    });
    return () => { cancelled = true; };
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // 🟥 Fix Bug Marketplace 2026-02-20 — Limpiar estado de creación al abrir/cerrar el picker.
  useEffect(() => {
    if (!pendingRecipe) {
      setCreateError(null);
      setIsCreatingApp(false);
    }
  }, [pendingRecipe]);

  const fetchUserApps = useCallback(async () => {
    setLoadingApps(true);
    // 🟥 CUADRO NEGRO 2026-06-27 (rev7) — Reset del error al re-fetch.
    setCreateError(null);
    try {
      // 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 — Paso 1: ESPERAR a que la
      // sesión global esté lista. Esto elimina la race condition que
      // generaba falsos 401 en Producción.
      await waitForSessionReady(2000);

      const token = getToken();
      const headers = { 'Content-Type': 'application/json' };
      // 🟥 FIX 401 prod feb-2026 — no mandar el placeholder cookie-session
      // como Bearer token (la cookie httpOnly viaja sola con credentials).
      if (token && token !== 'cookie-session') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      // 🟥 CUADRO NEGRO 2026-06-27 (rev7) — Backoff exponencial de 4
      // intentos (0/300/700/1500ms) en lugar de 1 retry de 300ms. Mata
      // el race condition de propagación de cookie en mobile/prod que
      // dejaba la lista de apps vacía y forzaba al usuario al modal de
      // "+ Crear app ahora" (donde encima salía el banner amarillo).
      // `credentials: 'include'` garantiza que la cookie httpOnly viaje
      // SIEMPRE — el backend la valida con SameSite=None + Secure.
      const doFetch = () => fetch(`${API}/apps`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      const backoffs = [0, 300, 700, 1500];
      let r = null;
      for (let i = 0; i < backoffs.length; i++) {
        if (backoffs[i] > 0) {
          await new Promise((res) => setTimeout(res, backoffs[i]));
        }
        r = await doFetch();
        if (r.status !== 401) break;
      }
      if (!r || !r.ok) {
        // Si llegamos acá con 401 tras 4 intentos, la sesión está REAL
        // expirada. Mostramos el banner amarillo con CTA real.
        if (r && r.status === 401) {
          // 🟥 CUADRO NEGRO 2026-06-27 (rev9) — Doble verificación contra
          // /api/auth/me ANTES de declarar la sesión muerta. En producción
          // observamos que /api/apps a veces 401-ea aunque la cookie sí
          // sigue válida para otros endpoints (CDN/WAF transitorio). Si
          // /auth/me responde 200 → mostramos banner clásico (problema
          // localizado en /apps). Si /auth/me también responde 401 →
          // la sesión está muerta de verdad: auto-redirect a /login con
          // `?next=<currentURL>` para que el usuario vuelva al mismo
          // contexto tras reloguearse, sin tener que rehacer su flow.
          try {
            const meRes = await fetch(`${API}/auth/me`, {
              method: 'GET',
              headers,
              credentials: 'include',
            });
            if (meRes.status === 401) {
              // Sesión muerta → relogin transparente.
              const back = encodeURIComponent(
                window.location.pathname + window.location.search,
              );
              window.location.href = `/login?next=${back}`;
              return;
            }
          } catch { /* network blip → caer al banner debajo */ }
          setCreateError(es
            ? 'No pudimos verificar tu sesión. Recargá la página o iniciá sesión otra vez.'
            : "We couldn't verify your session. Reload the page or sign in again.");
        }
        setUserApps([]);
      } else {
        const d = await r.json();
        setUserApps(Array.isArray(d?.items) ? d.items : []);
      }
    } catch {
      setUserApps([]);
    } finally {
      setLoadingApps(false);
    }
  }, [es]);

  const handleImport = useCallback(async (r) => {
    // 🟥 V18.0 — Premium Gate: importar recetas MODIFICA la app del
    // usuario, por lo tanto requiere Plan Premium.
    if (!isUserPremium()) {
      showPremiumGateMessage();
      return;
    }
    // 🟥 Fix UX 2026-02-19 — Si no hay appId (estamos fuera del Builder),
    // ofrecemos un selector con las apps del usuario para elegir destino.
    if (!appId) {
      setPendingRecipe(r);
      fetchUserApps();
      return;
    }
    setImportState((p) => ({ ...p, [r.id]: 'loading' }));
    const res = importMarketplaceRecipe(r, appId);
    setImportState((p) => ({ ...p, [r.id]: res.ok ? 'ok' : 'err' }));
  }, [appId, fetchUserApps]);

  // Elegir app destino desde el selector → importa + navega al Builder.
  const handleChooseApp = useCallback((targetAppId) => {
    if (!isUserPremium()) {
      showPremiumGateMessage();
      setPendingRecipe(null);
      return;
    }
    if (!pendingRecipe || !targetAppId) return;
    const res = importMarketplaceRecipe(pendingRecipe, targetAppId);
    if (res.ok) {
      setImportState((p) => ({ ...p, [pendingRecipe.id]: 'ok' }));
      setPendingRecipe(null);
      onClose?.();
      // 🟥 CUADRO NEGRO MAESTRO (sec 2) — el Marketplace NO puede abrir el
      // Builder. La receta queda importada; el Builder se abre a mano desde
      // /builder-projects.
      window.location.href = '/builder-projects';
    } else {
      setImportState((p) => ({ ...p, [pendingRecipe.id]: 'err' }));
    }
  }, [pendingRecipe, onClose]);

  const handleCreateAppAndImport = useCallback(async () => {
    // 🟥 Fix Bug Marketplace 2026-02-20 — Antes navegaba a `/my-apps` y rompía el
    // flujo (la página exigía sesión y mostraba "Sesión expirada" en falsos
    // positivos). Ahora creamos la app directamente vía POST /api/apps,
    // (opcional) importamos la receta pendiente al local store con el nuevo
    // appId y abrimos el Builder con hard-reload para garantizar montaje limpio.
    //
    // 🟥 CUADRO NEGRO 2026-02-25 — Retry-on-401 con 300ms para evitar falsos
    // "Tu sesión expiró" cuando la cookie httpOnly aún no estaba sincronizada
    // al primer fetch (SW warm-up, hydration race, etc). Solo mostramos el
    // banner amarillo de error si AMBOS intentos fallan con 401.
    if (isCreatingApp) return;
    if (pendingRecipe && !isUserPremium()) {
      showPremiumGateMessage();
      setPendingRecipe(null);
      return;
    }
    setIsCreatingApp(true);
    setCreateError(null);
    try {
      // 🟥 CUADRO NEGRO FIX FINAL 2026-02-26 — Eliminado el preflightAuth().
      // Razón: el preflight `/api/auth/me` añadía un punto de fallo extra.
      // Si /auth/me fallaba transitoriamente (cold start backend, network
      // blip, timeout), mostrábamos "Tu sesión expiró" SIN siquiera intentar
      // POST /api/apps. Era un falso positivo autoinducido por la "defensa
      // en profundidad". Ahora vamos directo a POST y reintentamos en caso
      // de 401 con backoff exponencial (300/700/1500ms). Solo después de 3
      // intentos fallidos consecutivos consideramos la sesión expirada.

      const token = getToken();
      // 🟥 CUADRO NEGRO 2026-02-26 — POST como CORS "simple" (sin preflight):
      // sin Content-Type: application/json, sin Authorization Bearer, sólo
      // credentials: include. El `name` viaja como query param. El backend
      // lo acepta tanto en body JSON como en ?name=.
      const headers = {};
      if (token && token !== 'cookie-session') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const desiredName = pendingRecipe?.name
        ? String(pendingRecipe.name).slice(0, 60)
        : (es ? 'Mi nueva app' : 'My new app');

      // 🟥 Retry-on-401 con backoff exponencial (4 intentos: 0/300/700/1500ms).
      const doFetch = () => fetch(`${API}/apps?name=${encodeURIComponent(desiredName)}`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
      const backoffs = [0, 300, 700, 1500];
      let res = null;
      for (let i = 0; i < backoffs.length; i++) {
        if (backoffs[i] > 0) {
          await new Promise((r) => setTimeout(r, backoffs[i]));
        }
        res = await doFetch();
        if (res.status !== 401) break; // éxito o error distinto → salir
      }

      if (!res.ok) {
        if (res.status === 401) {
          // 🟥 CUADRO NEGRO 2026-06-27 (rev9) — Doble verificación contra
          // /api/auth/me ANTES de declarar la sesión muerta. Si /auth/me
          // también devuelve 401 → auto-redirect a /login?next=<currentURL>.
          // Si /auth/me responde 200 → problema localizado en POST /apps,
          // mostramos el banner accionable.
          try {
            const meRes = await fetch(`${API}/auth/me`, {
              method: 'GET',
              headers,
              credentials: 'include',
            });
            if (meRes.status === 401) {
              const back = encodeURIComponent(
                window.location.pathname + window.location.search,
              );
              window.location.href = `/login?next=${back}`;
              return;
            }
          } catch { /* network blip → caer al banner debajo */ }
          setCreateError(es
            ? 'No pudimos verificar tu sesión. Recargá la página o iniciá sesión otra vez.'
            : "We couldn't verify your session. Reload the page or sign in again.");
        } else if (res.status === 403) {
          setCreateError(es ? 'No tenés permiso para crear apps.' : 'You do not have permission to create apps.');
        } else {
          setCreateError(es ? 'No pudimos crear la app. Probá de nuevo.' : "We couldn't create the app. Please try again.");
        }
        setIsCreatingApp(false);
        return;
      }
      const newApp = await res.json();
      const newAppId = newApp?.id;
      if (!newAppId) {
        setCreateError(es ? 'No pudimos crear la app. Probá de nuevo.' : "We couldn't create the app. Please try again.");
        setIsCreatingApp(false);
        return;
      }
      // Si había una receta pendiente, la importamos al nuevo appId.
      if (pendingRecipe) {
        try {
          importMarketplaceRecipe(pendingRecipe, newAppId);
          setImportState((p) => ({ ...p, [pendingRecipe.id]: 'ok' }));
        } catch {
          /* import al store local es best-effort; el Builder igual abre */
        }
      }
      setPendingRecipe(null);
      onClose?.();
      // 🟥 CUADRO NEGRO MAESTRO (sec 2) — prohibido abrir el Builder desde el
      // Marketplace: se vuelve a la página dedicada.
      window.location.href = '/builder-projects';
    } catch {
      setCreateError(es ? 'No se pudo conectar. Verificá tu red.' : 'Could not connect. Check your network.');
      setIsCreatingApp(false);
    }
  }, [isCreatingApp, pendingRecipe, es, onClose]);

  const handleLike = useCallback(async (r) => {
    // Optimistic update.
    setLikeState((p) => ({ ...p, [r.id]: (p[r.id] || 0) + 1 }));
    const res = await likeMarketplaceRecipe(r.id);
    if (!res.ok) {
      // Revertir + log a consola (no intrusivo).
      setLikeState((p) => ({ ...p, [r.id]: Math.max(0, (p[r.id] || 1) - 1) }));
      // eslint-disable-next-line no-console
      console.warn('[marketplace] like failed:', res.error);
    } else if (typeof res.likes === 'number') {
      // El backend es la fuente de verdad — incluye el dedup por user_id.
      setLikeState((p) => ({ ...p, [r.id]: res.likes }));
    }
  }, []);

  if (!open) return null;

  // 🟥 CUADRO NEGRO DEFINITIVO 2026-06-27 — createPortal a document.body +
  // z-index 999999. Esto garantiza que el Marketplace SIEMPRE aparezca
  // por encima de cualquier panel/overlay del layout (incluyendo el panel
  // del Genio que vive en su propio portal con z-[9999]) y NO quede
  // atrapado por ningún ancestor con transform/will-change/filter.
  return createPortal((
    <div
      data-testid="lyon-marketplace-panel"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[999999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-slate-950 border border-fuchsia-500/30 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-gradient-to-r from-fuchsia-950/50 to-cyan-950/40">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fuchsia-300" />
            {es ? 'Marketplace de Recetas IA' : 'AI Recipes Marketplace'}
            <span className="text-[10px] font-mono text-slate-400">({recipes.length})</span>
          </h3>
          <div className="flex items-center gap-1">
            {/* 🟦 CUADRO NEGRO #1 sec 2.3 — Mapa "Volver" oficial Marketplace → Genio.
                Mobile: solo icono visible; ≥sm: icono + texto. */}
            <button
              type="button"
              onClick={() => {
                onClose?.();
                navigate('/');
              }}
              data-testid="marketplace-back-to-genio"
              aria-label={es ? 'Volver al Genio' : 'Back to Genie'}
              className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold text-slate-300 hover:text-white bg-slate-800/60 hover:bg-slate-700"
              title={es ? 'Volver al Genio' : 'Back to Genie'}
            >
              <ChevronRight className="w-3 h-3 rotate-180" />
              <span className="hidden sm:inline">{es ? 'Volver' : 'Back'}</span>
            </button>
            {/* 🟦 CUADRO NEGRO #1 sec 2.2 — Navegación oficial Marketplace → Mis Proyectos del Builder.
                Mobile: solo icono visible; ≥sm: icono + texto. */}
            <button
              type="button"
              onClick={() => {
                onClose?.();
                navigate('/builder-projects');
              }}
              data-testid="marketplace-goto-projects"
              aria-label={es ? 'Ir a Mis Proyectos del Builder' : 'Go to My Builder Projects'}
              className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold text-cyan-200 hover:text-white bg-cyan-950/40 hover:bg-cyan-800/40 border border-cyan-500/30"
              title={es ? 'Ir a Mis Proyectos del Builder' : 'Go to My Builder Projects'}
            >
              <FolderOpen className="w-3 h-3" />
              <span className="hidden sm:inline">{es ? 'Mis Proyectos' : 'My Projects'}</span>
            </button>
            <button
              type="button"
              onClick={() => setExitModalOpen(true)}
              data-testid="lyon-close-marketplace"
              className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
              title={es ? 'Cerrar (Esc)' : 'Close (Esc)'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!hasAppContext && (
            <div
              data-testid="lyon-marketplace-no-app-banner"
              className="mb-3 rounded-lg border border-cyan-500/40 bg-cyan-950/30 px-3 py-2 flex items-start gap-2"
            >
              <Sparkles className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-[11px] font-semibold text-cyan-100">
                  {es ? 'Importá a cualquiera de tus apps' : 'Import into any of your apps'}
                </p>
                <p className="text-[10px] text-cyan-200/80 mt-0.5">
                  {es
                    ? 'Al pulsar "Importar a mi app" vas a poder elegir destino y DamyxIA te abrirá el Builder con la receta ya cargada.'
                    : 'Click "Import to my app" to pick a destination — DamyxIA will open the Builder with the recipe loaded.'}
                </p>
              </div>
            </div>
          )}
          {loading && (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {es ? 'Cargando recetas…' : 'Loading recipes…'}
            </div>
          )}
          {error && !loading && (
            <div className="text-center py-10 text-red-300 text-sm" data-testid="lyon-marketplace-error">
              {es ? `Error: ${error}` : `Error: ${error}`}
            </div>
          )}
          {!loading && !error && recipes.length === 0 && (
            <div className="text-center py-10 text-slate-500 text-sm">
              {es ? 'No hay recetas publicadas todavía.' : 'No published recipes yet.'}
            </div>
          )}

          {!loading && !error && mostLoved.length > 0 && (
            <div
              data-testid="marketplace-most-loved-section"
              className="mb-4 rounded-lg border border-pink-500/30 bg-gradient-to-br from-pink-950/30 to-fuchsia-950/30 p-3"
            >
              <p className="text-[11px] uppercase tracking-wider text-pink-300 font-bold mb-2 flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5" /> {es ? 'Más amadas esta semana' : 'Most loved this week'}
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {mostLoved.map((r) => (
                  <div
                    key={`ml-${r.id}`}
                    data-testid={`marketplace-most-loved-item-${r.id}`}
                    className="shrink-0 min-w-[160px] rounded-md bg-slate-900/70 border border-pink-500/30 px-3 py-2"
                  >
                    <p className="text-xs font-bold text-white truncate">{r.name}</p>
                    <p className="text-[10px] text-pink-200 flex items-center gap-1 mt-0.5">
                      <Heart className="w-2.5 h-2.5" /> {r.likes_recent} {es ? 'esta semana' : 'this week'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {recipes.map((r) => {
              const ist = importState[r.id];
              const likes = likeState[r.id] ?? r.likes ?? 0;
              return (
                <div
                  key={r.id}
                  data-testid={`marketplace-item-${r.id}`}
                  className={`border border-slate-700/60 rounded-lg bg-slate-900/60 p-3 hover:border-fuchsia-500/60 transition-colors flex flex-col gap-2 ${r.isTrending ? 'animate-pulse-soft' : ''}`}
                >
                  {/* 🟦 V11.9 — AI preview image (lazy load, fallback a placeholder neutral si falta URL). */}
                  {r.previewImageUrl ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL || ''}${r.previewImageUrl}`}
                      alt={r.name}
                      loading="lazy"
                      data-testid={`marketplace-preview-${r.id}`}
                      onError={(e) => {
                        // Si la imagen falla, ocultamos el <img> roto y mostramos placeholder.
                        e.currentTarget.style.display = 'none';
                        const ph = e.currentTarget.nextElementSibling;
                        if (ph) ph.style.display = 'flex';
                      }}
                      className="w-full h-24 object-cover rounded-md bg-slate-800/60 border border-slate-700/40"
                    />
                  ) : null}
                  <div
                    data-testid={`marketplace-preview-placeholder-${r.id}`}
                    aria-hidden="true"
                    style={{ display: r.previewImageUrl ? 'none' : 'flex' }}
                    className="w-full h-24 rounded-md bg-slate-800/60 border border-slate-700/40 items-center justify-center text-slate-500"
                  >
                    <Sparkles className="w-6 h-6 opacity-50" />
                  </div>
                  {r.isTrending && (
                    <span
                      data-testid={`marketplace-trending-pulse-${r.id}`}
                      aria-hidden="true"
                      className="sr-only"
                    >
                      trending pulse active
                    </span>
                  )}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-bold text-white truncate" data-testid={`marketplace-item-name-${r.id}`}>
                          {r.name}
                        </p>
                        {r.isTrending && (
                          <span
                            data-testid={`marketplace-badge-trending-${r.id}`}
                            className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-orange-500 to-pink-500 text-white flex items-center gap-1 shrink-0"
                            title={es ? 'En tendencia' : 'Trending'}
                          >
                            <TrendingUp className="w-2.5 h-2.5" /> {es ? 'Trending' : 'Trending'}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 font-mono">
                        &lt;{r.nodeType}&gt; · {r.steps?.length || 0} {es ? 'pasos' : 'steps'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleLike(r)}
                      data-testid={`marketplace-like-${r.id}`}
                      className="flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800 hover:bg-pink-900/60 text-pink-300 text-[11px] transition-colors"
                      title={es ? 'Me gusta' : 'Like'}
                    >
                      <Heart className="w-3 h-3" />
                      <span data-testid={`marketplace-like-count-${r.id}`}>{likes}</span>
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-300 line-clamp-2">{r.description}</p>
                  {Array.isArray(r.tags) && r.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {r.tags.slice(0, 4).map((t) => (
                        <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-800/70 text-cyan-300 border border-slate-700/60 flex items-center gap-1">
                          <Tag className="w-2 h-2" /> {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleImport(r)}
                    disabled={ist === 'loading' || ist === 'ok'}
                    data-testid={`marketplace-import-${r.id}`}
                    title={!hasAppContext
                      ? (es ? 'Elegí a qué app importar' : 'Pick which app to import into')
                      : (es ? 'Importar a mi app' : 'Import to my app')}
                    className={`mt-1 w-full px-2 py-1.5 rounded-md text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                      ist === 'ok' ? 'bg-emerald-900/40 text-emerald-200 cursor-default' :
                      ist === 'err' ? 'bg-red-900/40 text-red-200' :
                      ist === 'loading' ? 'bg-fuchsia-900/40 text-fuchsia-200 cursor-wait' :
                      'bg-fuchsia-600 hover:bg-fuchsia-500 text-white'
                    }`}
                  >
                    <Download className="w-3 h-3" />
                    {ist === 'ok' ? (es ? 'Importada' : 'Imported') :
                     ist === 'err' ? (es ? 'Error' : 'Error') :
                     ist === 'loading' ? (es ? 'Importando…' : 'Importing…') :
                     (es ? 'Importar a mi app' : 'Import to my app')}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 🟥 Fix UX 2026-02-19 — Selector de app destino cuando se importa
          fuera del Builder. */}
      {pendingRecipe && (
        <div
          data-testid="marketplace-app-picker"
          onClick={(e) => { if (e.target === e.currentTarget) setPendingRecipe(null); }}
          className="fixed inset-0 z-[1000000] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-slate-950 border border-fuchsia-500/40 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-3 sm:px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-gradient-to-r from-fuchsia-950/60 to-cyan-950/40 gap-2">
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                {/* 🟦 CUADRO NEGRO #1 sec 4 — Mapa "Volver": Modal Importar → Marketplace.
                    Mobile: solo icono; ≥sm: icono + texto. */}
                <button
                  type="button"
                  onClick={() => setPendingRecipe(null)}
                  data-testid="marketplace-app-picker-back"
                  aria-label={es ? 'Volver al Marketplace' : 'Back to Marketplace'}
                  className="flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded text-[11px] font-semibold text-slate-300 hover:text-white bg-slate-800/60 hover:bg-slate-700 shrink-0"
                  title={es ? 'Volver al Marketplace' : 'Back to Marketplace'}
                >
                  <ChevronRight className="w-3 h-3 rotate-180" />
                  <span className="hidden sm:inline">{es ? 'Volver' : 'Back'}</span>
                </button>
                <FolderOpen className="w-4 h-4 text-fuchsia-300 shrink-0" />
                <h3 className="text-xs sm:text-sm font-bold text-white truncate">
                  {es ? '¿A cuál app importar?' : 'Import into which app?'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setPendingRecipe(null)}
                data-testid="marketplace-app-picker-close"
                className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
                title={es ? 'Cancelar' : 'Cancel'}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <p className="text-[11px] text-slate-400 mb-3 px-1">
                {es
                  ? <>Vamos a importar <span className="font-semibold text-fuchsia-300">{pendingRecipe.name}</span> y abrir el Builder de la app que elijas.</>
                  : <>We will import <span className="font-semibold text-fuchsia-300">{pendingRecipe.name}</span> and open the Builder of the app you pick.</>}
              </p>
              {createError && (
                <div
                  data-testid="marketplace-create-error"
                  className="mb-3 rounded-lg border border-yellow-500/40 bg-yellow-950/30 px-3 py-2 flex items-start gap-2"
                  role="alert"
                >
                  <AlertTriangle className="w-4 h-4 text-yellow-300 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-yellow-100 leading-snug">{createError}</p>
                    {/* 🟥 CUADRO NEGRO 2026-06-27 (rev7) — CTAs concretas
                        en vez de texto pasivo. "Reintentar" vuelve a
                        disparar fetchUserApps con el backoff completo.
                        "Iniciar sesión" navega al login (preservando
                        return-to actual). */}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fetchUserApps()}
                        className="text-[11px] font-semibold text-yellow-100 hover:text-white underline underline-offset-2 transition-colors"
                        data-testid="marketplace-error-retry"
                      >
                        {es ? 'Reintentar' : 'Retry'}
                      </button>
                      <span className="text-yellow-500/40 text-[11px]">·</span>
                      <button
                        type="button"
                        onClick={() => {
                          try {
                            const back = encodeURIComponent(window.location.pathname + window.location.search);
                            window.location.href = `/login?next=${back}`;
                          } catch {
                            window.location.href = '/login';
                          }
                        }}
                        className="text-[11px] font-semibold text-yellow-100 hover:text-white underline underline-offset-2 transition-colors"
                        data-testid="marketplace-error-relogin"
                      >
                        {es ? 'Iniciar sesión otra vez' : 'Sign in again'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {loadingApps ? (
                <div className="flex items-center justify-center py-10 text-slate-400 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {es ? 'Cargando tus apps…' : 'Loading your apps…'}
                </div>
              ) : userApps.length === 0 ? (
                <div className="text-center py-6 px-3">
                  <FolderOpen className="w-10 h-10 mx-auto text-slate-600 mb-2" />
                  <p className="text-xs text-slate-300 font-semibold mb-1">
                    {es ? 'Todavía no tenés apps del Builder' : 'You have no Builder apps yet'}
                  </p>
                  <p className="text-[11px] text-slate-500 mb-4">
                    {es ? 'Creá tu primera app para poder importar recetas.' : 'Create your first app to import recipes.'}
                  </p>
                  <button
                    type="button"
                    onClick={handleCreateAppAndImport}
                    disabled={isCreatingApp}
                    data-testid="marketplace-app-picker-create"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-500 hover:to-fuchsia-500 text-white text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isCreatingApp ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        {es ? 'Creando…' : 'Creating…'}
                      </>
                    ) : (
                      <>
                        <Plus className="w-3.5 h-3.5" />
                        {es ? 'Crear app ahora' : 'Create app now'}
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {userApps.map((app) => (
                    <li key={app.id}>
                      <button
                        type="button"
                        onClick={() => handleChooseApp(app.id)}
                        data-testid={`marketplace-app-picker-item-${app.id}`}
                        className="w-full text-left px-3 py-2.5 rounded-lg bg-slate-900/60 hover:bg-fuchsia-900/30 border border-slate-700/60 hover:border-fuchsia-500/50 text-slate-200 hover:text-white text-xs flex items-center gap-2.5 transition-colors group"
                      >
                        <FolderOpen className="w-4 h-4 text-fuchsia-400 shrink-0" />
                        <span className="flex-1 truncate font-medium">{app.name}</span>
                        <span className="text-[10px] font-mono text-slate-500 group-hover:text-fuchsia-300 shrink-0">
                          {app.status}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-fuchsia-300 shrink-0" />
                      </button>
                    </li>
                  ))}
                  <li className="pt-2 mt-2 border-t border-slate-800/60">
                    <button
                      type="button"
                      onClick={handleCreateAppAndImport}
                      disabled={isCreatingApp}
                      data-testid="marketplace-app-picker-create-new"
                      className="w-full text-left px-3 py-2.5 rounded-lg bg-slate-900/40 hover:bg-indigo-900/30 border border-dashed border-slate-700/60 hover:border-indigo-500/50 text-slate-300 hover:text-white text-xs flex items-center gap-2.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isCreatingApp ? (
                        <Loader2 className="w-4 h-4 text-indigo-400 shrink-0 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 text-indigo-400 shrink-0" />
                      )}
                      <span className="flex-1">
                        {isCreatingApp
                          ? (es ? 'Creando app…' : 'Creating app…')
                          : (es ? 'Crear una app nueva…' : 'Create a new app…')}
                      </span>
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🟦 CUADRO NEGRO #1 FASE 3 sec 3.2 — Modal X universal en Marketplace.
          Cuadro Negro Nav-Loop fix: SIEMPRE navega a `/` (Genio) tras cerrar
          para evitar el loop circular Marketplace↔Mis Apps↔Marketplace. */}
      <ExitConfirmModal
        open={exitModalOpen}
        onCancel={() => setExitModalOpen(false)}
        onExitNoSave={() => {
          setExitModalOpen(false);
          onClose?.();
          navigate('/');
        }}
        es={es}
        description={es
          ? '¿Cerrar el Marketplace y volver al Genio?'
          : 'Close the Marketplace and return to the Genie?'}
        testIdPrefix="marketplace-exit"
      />
    </div>
  ), document.body);
};

export default MarketplacePanel;
